import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllClients, getVersionThresholds, saveVersionThresholds } from '../utils/apiService';
import { 
  defaultVersionThresholds, 
  getServersWithVersionIssues,
  updateVersionThreshold,
  loadCustomThresholds,
  getCurrentThresholds
} from '../data/versionThresholds';

const VersionDashboard = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serversWithIssues, setServersWithIssues] = useState([]);
  const [includeOutdated, setIncludeOutdated] = useState(false);
  const [thresholds, setThresholds] = useState({...defaultVersionThresholds});
  const [editingThreshold, setEditingThreshold] = useState(null);
  const [viewMode, setViewMode] = useState('issues'); // 'issues' ou 'thresholds'
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });
  const [filters, setFilters] = useState({
    mariadb: '',
    apache: '',
    nodejs: '',
    gitPatientAppVersion: '',
    gitManagerFrontAppVersion: '',
    gitMarshallerVersion: '',
    gitMarshallerInteropVersion: ''
  });
  
  // Composants à afficher pour le tableau des seuils
  const componentLabels = {
    mariadb: 'MariaDB',
    apache: 'Apache',
    nodejs: 'NodeJS',
    gitPatientAppVersion: 'Patient App',
    gitManagerFrontAppVersion: 'Manager Front App',
    gitMarshallerVersion: 'Marshaller',
    gitMarshallerInteropVersion: 'Marshaller Interop'
  };

  useEffect(() => {
    const loadData = async () => {
      // Charger les seuils personnalisés depuis le serveur
      const customThresholds = await getVersionThresholds();
      const mergedThresholds = loadCustomThresholds(customThresholds);
      setThresholds(mergedThresholds);
      
      // Charger les clients
      const clientsData = await getAllClients();
      setClients(clientsData);
      
      // Calculer les serveurs avec des problèmes
      const issues = getServersWithVersionIssues(clientsData, includeOutdated);
      setServersWithIssues(issues);
      
      setLoading(false);
    };
    
    loadData();
  }, [includeOutdated]);
  
  // Fonction pour mettre à jour un seuil de version
  const handleThresholdUpdate = async (component, field, value) => {
    const updatedThresholds = {...thresholds};
    updatedThresholds[component][field] = value;
    
    // Mettre à jour l'état local
    setThresholds(updatedThresholds);
    
    // Mettre à jour la configuration globale
    updateVersionThreshold(component, field, value);
    
    // Si on n'est plus en mode édition, sauvegarder
    if (editingThreshold === component && field === 'save') {
      try {
        await saveVersionThresholds(getCurrentThresholds());
        setSaveMessage({ type: 'success', text: 'Seuils sauvegardés avec succès !' });
        setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
      } catch (error) {
        setSaveMessage({ type: 'error', text: 'Erreur lors de la sauvegarde des seuils' });
        setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
      }
    }
    
    // Recalculer les serveurs avec des problèmes
    const issues = getServersWithVersionIssues(clients, includeOutdated);
    setServersWithIssues(issues);
  };
  
  // Fonction pour activer/désactiver l'édition d'un seuil
  const toggleThresholdEdit = async (component) => {
    if (editingThreshold === component) {
      // Sauvegarder les modifications
      await handleThresholdUpdate(component, 'save', true);
      setEditingThreshold(null);
    } else {
      setEditingThreshold(component);
    }
  };
  
  // Fonction pour filtrer les serveurs avec des problèmes
  const getFilteredServersWithIssues = () => {
    return serversWithIssues.filter(serverIssue => {
      // Vérifier si au moins un composant correspond aux filtres
      return serverIssue.componentIssues.some(issue => {
        const filter = filters[issue.component];
        if (!filter) return true; // Pas de filtre = afficher
        
        // Vérifier si la version correspond au filtre
        return issue.version.toLowerCase().includes(filter.toLowerCase());
      });
    });
  };
  
  // Fonction pour formater une version pour l'affichage
  const formatVersion = (version) => {
    if (!version) return 'Non spécifié';
    return version;
  };
  
  // Obtenir une couleur pour un niveau d'alerte
  const getAlertColor = (level) => {
    switch(level) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'warning': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'info': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };
  
  // Obtenir un badge de niveau d'alerte
  const getAlertBadge = (level) => {
    let badgeClass = '';
    let badgeText = '';
    
    switch(level) {
      case 'critical':
        badgeClass = 'bg-red-600 text-white';
        badgeText = 'Critique';
        break;
      case 'warning':
        badgeClass = 'bg-amber-500 text-white';
        badgeText = 'Avertissement';
        break;
      case 'info':
        badgeClass = 'bg-blue-500 text-white';
        badgeText = 'Info';
        break;
      default:
        badgeClass = 'bg-gray-400 text-white';
        badgeText = 'Normal';
    }
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${badgeClass}`}>
        {badgeText}
      </span>
    );
  };
  
  // Obtenir le texte d'un composant à partir de sa clé
  const getComponentName = (key) => {
    return componentLabels[key] || key;
  };
  
  const filteredServersWithIssues = getFilteredServersWithIssues();
  
  if (loading) {
    return <div className="text-center py-10">Chargement des données...</div>;
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Tableau de bord des versions</h1>
      
      <div className="bg-white shadow-md rounded-lg mb-8">
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex space-x-4">
              <button
                className={`px-4 py-2 rounded-lg ${viewMode === 'issues' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                onClick={() => setViewMode('issues')}
              >
                Alertes de versions
              </button>
              <button
                className={`px-4 py-2 rounded-lg ${viewMode === 'thresholds' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                onClick={() => setViewMode('thresholds')}
              >
                Seuils de version
              </button>
            </div>
            
            {viewMode === 'issues' && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="includeOutdated"
                  className="mr-2"
                  checked={includeOutdated}
                  onChange={() => setIncludeOutdated(!includeOutdated)}
                />
                <label htmlFor="includeOutdated">Inclure versions non critiques à mettre à jour</label>
              </div>
            )}
          </div>
        </div>
        
        {/* Vue des alertes de versions */}
        {viewMode === 'issues' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">
              Alertes de versions ({filteredServersWithIssues.length} serveurs concernés)
            </h2>
            
            {/* Filtres par composant */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Filtrer par version de composant</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.keys(componentLabels).map(component => (
                  <div key={component}>
                    <label className="block text-xs text-gray-600 mb-1">
                      {componentLabels[component]}
                    </label>
                    <input
                      type="text"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="ex: 10.9"
                      value={filters[component]}
                      onChange={(e) => setFilters({...filters, [component]: e.target.value})}
                    />
                  </div>
                ))}
              </div>
              <button
                className="mt-3 text-sm text-blue-600 hover:text-blue-800"
                onClick={() => setFilters({
                  mariadb: '',
                  apache: '',
                  nodejs: '',
                  gitPatientAppVersion: '',
                  gitManagerFrontAppVersion: '',
                  gitMarshallerVersion: '',
                  gitMarshallerInteropVersion: ''
                })}
              >
                Réinitialiser les filtres
              </button>
            </div>
            
            {filteredServersWithIssues.length === 0 ? (
              <div className="text-center py-8 bg-green-50 rounded-lg border border-green-200">
                <svg className="mx-auto h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <p className="mt-2 text-lg text-green-600 font-medium">
                  {serversWithIssues.length === 0 
                    ? 'Tous les serveurs sont à jour !'
                    : 'Aucun serveur ne correspond aux filtres'}
                </p>
                <p className="text-green-500">
                  {serversWithIssues.length === 0 
                    ? 'Aucun problème de version n\'a été détecté.'
                    : `${serversWithIssues.length} serveur(s) avec des problèmes, mais filtré(s).`}
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredServersWithIssues.map((serverIssue, index) => (
                  <div 
                    key={`${serverIssue.clientId}-${serverIssue.serverIndex}`} 
                    className={`p-4 rounded-lg border ${serverIssue.hasCritical ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">
                          {serverIssue.clientName} - {serverIssue.serverName || `Serveur ${serverIssue.serverIndex + 1}`}
                        </h3>
                        <div className="flex items-center mt-1">
                          <span 
                            className="px-2 py-1 text-xs rounded font-medium mr-2" 
                            style={{ backgroundColor: serverIssue.serverColor || '#3B82F6', color: 'white' }}
                          >
                            {serverIssue.serverType}
                          </span>
                          {serverIssue.hasCritical && (
                            <span className="bg-red-600 text-white px-2 py-1 text-xs rounded-full font-medium">
                              Mise à jour requise
                            </span>
                          )}
                        </div>
                      </div>
                      <Link 
                        to={`/client/${serverIssue.clientId}#server-${serverIssue.serverIndex}`}
                        className="bg-white text-blue-600 border border-blue-300 hover:bg-blue-50 px-3 py-1 rounded text-sm"
                      >
                        Voir détails
                      </Link>
                    </div>
                    
                    <div className="mt-4">
                      <h4 className="font-medium text-sm text-gray-700 mb-2">Composants à mettre à jour:</h4>
                      <div className="space-y-2">
                        {serverIssue.componentIssues.map((issue, issueIndex) => (
                          <div 
                            key={issueIndex} 
                            className={`p-3 rounded ${getAlertColor(issue.alertLevel)}`}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center">
                                  <span className="font-medium">{getComponentName(issue.component)}:</span>
                                  <span className="ml-2 font-mono">{issue.version}</span>
                                  <span className="ml-2">{getAlertBadge(issue.alertLevel)}</span>
                                </div>
                                <p className="text-sm mt-1">{issue.message}</p>
                                <p className="text-sm font-medium mt-1">{issue.recommendation}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Vue des seuils de version */}
        {viewMode === 'thresholds' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Configuration des seuils de version</h2>
            
            {/* Message de sauvegarde */}
            {saveMessage.text && (
              <div className={`mb-4 p-3 rounded-lg ${
                saveMessage.type === 'success' 
                  ? 'bg-green-100 text-green-700 border border-green-300' 
                  : 'bg-red-100 text-red-700 border border-red-300'
              }`}>
                {saveMessage.text}
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="py-3 px-4 text-left border-b">Composant</th>
                    <th className="py-3 px-4 text-left border-b">Version minimale</th>
                    <th className="py-3 px-4 text-left border-b">Version recommandée</th>
                    <th className="py-3 px-4 text-left border-b">Critique</th>
                    <th className="py-3 px-4 text-left border-b">Description</th>
                    <th className="py-3 px-4 text-left border-b">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(thresholds).map((component) => (
                    <tr key={component} className="hover:bg-gray-50 border-b">
                      <td className="py-3 px-4 font-medium">{getComponentName(component)}</td>
                      <td className="py-3 px-4">
                        {editingThreshold === component ? (
                          <input
                            type="text"
                            className="w-full p-1 border border-gray-300 rounded"
                            value={thresholds[component].minVersion}
                            onChange={(e) => handleThresholdUpdate(component, 'minVersion', e.target.value)}
                          />
                        ) : (
                          <code className="bg-gray-100 px-2 py-1 rounded">{thresholds[component].minVersion}</code>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {editingThreshold === component ? (
                          <input
                            type="text"
                            className="w-full p-1 border border-gray-300 rounded"
                            value={thresholds[component].recommendedVersion}
                            onChange={(e) => handleThresholdUpdate(component, 'recommendedVersion', e.target.value)}
                          />
                        ) : (
                          <code className="bg-gray-100 px-2 py-1 rounded">{thresholds[component].recommendedVersion}</code>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {editingThreshold === component ? (
                          <input
                            type="checkbox"
                            checked={thresholds[component].critical}
                            onChange={(e) => handleThresholdUpdate(component, 'critical', e.target.checked)}
                          />
                        ) : (
                          <span className={thresholds[component].critical ? 'text-red-600 font-medium' : 'text-gray-500'}>
                            {thresholds[component].critical ? 'Oui' : 'Non'}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {editingThreshold === component ? (
                          <textarea
                            className="w-full p-1 border border-gray-300 rounded"
                            value={thresholds[component].description}
                            onChange={(e) => handleThresholdUpdate(component, 'description', e.target.value)}
                          />
                        ) : (
                          <span className="text-sm">{thresholds[component].description}</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          className={`px-3 py-1 rounded text-sm ${
                            editingThreshold === component
                              ? 'bg-green-600 text-white hover:bg-green-700'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                          onClick={() => toggleThresholdEdit(component)}
                        >
                          {editingThreshold === component ? 'Enregistrer' : 'Modifier'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Comment utiliser les seuils de version</h3>
              <ul className="list-disc list-inside text-blue-700 space-y-1">
                <li>La <strong>version minimale</strong> est le seuil en dessous duquel une alerte sera générée</li>
                <li>La <strong>version recommandée</strong> est la version vers laquelle il est conseillé de mettre à jour</li>
                <li>L'option <strong>Critique</strong> détermine si un problème de version est considéré comme critique (rouge) ou simplement un avertissement (orange)</li>
                <li>La <strong>Description</strong> est affichée dans les alertes pour expliquer pourquoi la mise à jour est nécessaire</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VersionDashboard;